// @oasis-permission-inherits: /projects (projects:view)
import { useOnChangeEffect } from "@mirage-web/utils/react-helpers";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { LayoutGrid, Monitor, Search, Smartphone, Tent } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";

import { InfiniteQueryPagination } from "~/components/infinite-query-pagination";
import { PermissionBanner } from "~/components/permission-banner";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useAuthStore } from "~/modules/auth/auth-store";
import ResponsiveGrid from "~/modules/mirage/components/responsive-grid";
import { FailedGenerationsSection } from "~/modules/projects/components/failed-generations-section";
import { ProjectCard } from "~/modules/projects/components/project-card";
import { fetchProjectsQuery } from "~/modules/projects/queries";
import {
  projectCreationSourceFilterSchema,
  projectPlatformFilterSchema,
} from "~/modules/projects/schemas";

const ALL_FILTER_VALUE = "all";

const PLATFORM_FILTER_OPTIONS = [
  { value: ALL_FILTER_VALUE, label: "All", Icon: LayoutGrid },
  { value: "ios", label: "iOS", Icon: Smartphone },
  { value: "web", label: "Web", Icon: Monitor },
] as const;

const CREATION_SOURCE_FILTER_OPTIONS = [
  { value: ALL_FILTER_VALUE, label: "Any", Icon: null },
  { value: "basecamp", label: "Basecamp", Icon: Tent },
] as const;

export const Route = createFileRoute("/_dash/projects/")({
  validateSearch: z.object({
    userId: z.string().optional(),
    projectId: z.string().optional(),
    platform: projectPlatformFilterSchema.optional().catch(undefined),
    source: projectCreationSourceFilterSchema.optional().catch(undefined),
    page: z.number().optional(),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { userId, projectId, platform, source, page } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [user] = useAuthStore((s) => [s.user]);
  const [userIdInput, setUserIdInput] = useState(userId ?? "");
  const [projectIdInput, setProjectIdInput] = useState(projectId ?? "");
  const canViewProjectFeed = user?.permissions.includes("projects:feed-view") ?? false;
  const canViewProjects = user?.permissions.includes("projects:view") ?? false;
  const hasSearchFilters = Boolean(userId || projectId);
  const shouldFetchProjects = hasSearchFilters ? canViewProjects : canViewProjectFeed;

  useOnChangeEffect(userId, (value) => {
    setUserIdInput(value ?? "");
  });

  useOnChangeEffect(projectId, (value) => {
    setProjectIdInput(value ?? "");
  });

  const { data, error, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, refetch } =
    fetchProjectsQuery.useInfiniteQuery(
      {
        params: {
          filters: {
            ...(userId ? { userId } : {}),
            ...(projectId ? { projectId } : {}),
            ...(platform ? { platform } : {}),
            ...(source ? { creationSource: source } : {}),
          },
        },
      },
      {
        enabled: shouldFetchProjects,
      }
    );

  const totalPages = data?.pages.length ?? 0;
  const targetPageIndex = page ? page - 1 : 0;
  const [currentPageIndex, setCurrentPageIndex] = useState(targetPageIndex);

  useEffect(() => {
    setCurrentPageIndex(0);
  }, [userId, projectId, platform, source]);

  useEffect(() => {
    if ((userId || projectId) && shouldFetchProjects) {
      void refetch();
    }
  }, [userId, projectId, shouldFetchProjects, refetch]);

  useEffect(() => {
    if (
      shouldFetchProjects &&
      targetPageIndex >= totalPages &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    targetPageIndex,
    totalPages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    shouldFetchProjects,
  ]);

  useEffect(() => {
    if (targetPageIndex < totalPages) {
      setCurrentPageIndex(targetPageIndex);
    }
  }, [targetPageIndex, totalPages]);

  const currentPageData = data?.pages[currentPageIndex] ?? data?.pages[0];

  const searchParams = {
    userId,
    projectId,
    platform,
    source,
  };
  const hasActiveFilters = Boolean(userId || projectId || platform || source);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCurrentPageIndex(0);
    navigate({
      search: {
        userId: userIdInput.trim() || undefined,
        projectId: projectIdInput.trim() || undefined,
        platform,
        source,
        page: 1,
      },
    });
  };

  const onPlatformChange = (value: string) => {
    setCurrentPageIndex(0);
    navigate({
      search: {
        ...searchParams,
        platform: value === ALL_FILTER_VALUE ? undefined : projectPlatformFilterSchema.parse(value),
        page: 1,
      },
    });
  };

  const onSourceChange = (value: string) => {
    setCurrentPageIndex(0);
    navigate({
      search: {
        ...searchParams,
        source:
          value === ALL_FILTER_VALUE ? undefined : projectCreationSourceFilterSchema.parse(value),
        page: 1,
      },
    });
  };

  const clearFilters = () => {
    setUserIdInput("");
    setProjectIdInput("");
    setCurrentPageIndex(0);
    navigate({ search: {} });
  };

  const onPreviousPage = () => {
    const previousPageIndex = Math.max(0, currentPageIndex - 1);
    setCurrentPageIndex(previousPageIndex);
    navigate({
      search: {
        ...searchParams,
        page: previousPageIndex + 1,
      },
    });
  };

  const onNextPage = async () => {
    const nextPageIndex = currentPageIndex + 1;
    setCurrentPageIndex(nextPageIndex);
    navigate({
      search: {
        ...searchParams,
        page: nextPageIndex + 1,
      },
    });
    if (nextPageIndex === totalPages) {
      await fetchNextPage();
    }
  };

  const onPageChange = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    navigate({
      search: {
        ...searchParams,
        page: pageIndex + 1,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Projects</h1>
          <p className="text-sm text-muted-foreground">Captions Projects</p>
        </div>
        {canViewProjects && (
          <form onSubmit={onSubmit} className="flex flex-wrap items-center gap-2">
            <Input
              value={userIdInput}
              onChange={(event) => setUserIdInput(event.currentTarget.value)}
              placeholder="User ID"
              className="w-56"
              autoComplete="off"
              autoCorrect="off"
              data-1p-ignore
            />
            <Input
              value={projectIdInput}
              onChange={(event) => setProjectIdInput(event.currentTarget.value)}
              placeholder="Project ID"
              className="w-56"
              autoComplete="off"
              autoCorrect="off"
              data-1p-ignore
            />
            <Button type="submit" size="sm" className="gap-1.5">
              <Search className="size-4" aria-hidden="true" />
              Search
            </Button>
            {hasActiveFilters && (
              <Button type="button" size="sm" variant="outline" onClick={clearFilters}>
                Clear
              </Button>
            )}
          </form>
        )}
      </div>
      {(canViewProjectFeed || canViewProjects) && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <Tabs value={platform ?? ALL_FILTER_VALUE} onValueChange={onPlatformChange}>
            <FilterTabsRow label="Platform">
              <TabsList>
                {PLATFORM_FILTER_OPTIONS.map(({ value, label, Icon }) => (
                  <TabsTrigger key={value} value={value}>
                    <Icon aria-hidden="true" />
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </FilterTabsRow>
          </Tabs>
          <Tabs value={source ?? ALL_FILTER_VALUE} onValueChange={onSourceChange}>
            <FilterTabsRow label="Created via">
              <TabsList>
                {CREATION_SOURCE_FILTER_OPTIONS.map(({ value, label, Icon }) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    className={
                      value === "basecamp" ? "data-[state=active]:text-chart-3" : undefined
                    }
                  >
                    {Icon && <Icon aria-hidden="true" />}
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </FilterTabsRow>
          </Tabs>
        </div>
      )}
      {hasSearchFilters && !canViewProjects && (
        <PermissionBanner requiredPermission="projects:view" />
      )}
      {!hasSearchFilters && !canViewProjectFeed && canViewProjects && (
        <div className="text-sm text-muted-foreground">
          Search by user ID or project ID to view projects.
        </div>
      )}
      {shouldFetchProjects && totalPages > 0 && (
        <div className="flex justify-end">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      )}

      {shouldFetchProjects && (
        <ResponsiveGrid gap={16} maxColumns={5} itemMinWidth={210} itemMinHeight={300}>
          {(isLoading || isFetchingNextPage) &&
            Array.from({ length: 12 }).map((_, index) => (
              <Skeleton key={index} className="min-h-[300px] w-full rounded-md" />
            ))}
          {currentPageData?.projects.page.map((project) => (
            <ProjectCard key={project.id} project={project} canOpenProject={canViewProjects} />
          ))}
          {error && (
            <div className="text-sm text-destructive">
              {error instanceof Error ? error.message : "Failed to load projects"}
            </div>
          )}
          {!isLoading && !error && (currentPageData?.projects.page.length ?? 0) === 0 && (
            <div className="text-sm text-muted-foreground">
              {hasNextPage && currentPageIndex === totalPages - 1
                ? "No matches among the projects scanned for this page. Go to the next page to keep looking."
                : "No projects found"}
            </div>
          )}
        </ResponsiveGrid>
      )}

      {userId && canViewProjects && <FailedGenerationsSection userId={userId} />}

      {shouldFetchProjects && totalPages > 0 && !isLoading && !isFetchingNextPage && (
        <div className="flex justify-end pb-12">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}

function FilterTabsRow({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}
